import { useState, useEffect, useCallback, useRef } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import {
  Loader2, LogOut, MapPin, Shield, Calendar, Clock, CalendarDays, Send, Lock, MessageSquare,
  Medal, Star, Trophy, Heart, Crown, Flame, Award, Users, Check, X, Megaphone, Trash2, ShieldAlert,
} from "lucide-react";
import { Input } from "@/components/ui/input";
import { PORTAL_TOKEN_KEY } from "@/components/MemberLoginModal";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const authHeader = () => ({ Authorization: `Bearer ${localStorage.getItem(PORTAL_TOKEN_KEY)}` });
const initials = (name) => name.split(" ").map((p) => p[0]).join("").slice(0, 2).toUpperCase();
const fmtTime = (iso) => { try { return new Date(iso).toLocaleTimeString("de-CH", { hour: "2-digit", minute: "2-digit" }); } catch { return ""; } };
const fmtDate = (d) => { if (!d) return ""; try { return new Date(d + "T00:00:00").toLocaleDateString("de-CH", { weekday: "short", day: "2-digit", month: "long", year: "numeric" }); } catch { return d; } };

const AWARD_ICONS = { medal: Medal, star: Star, trophy: Trophy, shield: Shield, heart: Heart, crown: Crown, flame: Flame, award: Award };

const ProfileCard = ({ user, onLogout }) => {
  const [awards, setAwards] = useState(null);
  useEffect(() => {
    axios.get(`${API}/portal/awards`, { headers: authHeader() })
      .then(({ data }) => setAwards(data))
      .catch(() => setAwards([]));
  }, []);

  return (
    <div className="rounded-2xl border border-gold/20 bg-white/[0.03] p-6" data-testid="portal-profile">
      <div className="flex items-center gap-4">
        <div className="w-16 h-16 rounded-full border border-gold/40 flex items-center justify-center flex-shrink-0" style={{ background: "radial-gradient(circle at 30% 25%, rgba(229,199,107,0.18), rgba(198,156,34,0.06))" }}>
          <span className="font-accent italic text-2xl font-medium" style={{ color: "#E5C76B" }}>{initials(user.name)}</span>
        </div>
        <div className="min-w-0">
          <p className="text-xs uppercase tracking-[0.22em] font-semibold" style={{ color: "#C69C22" }}>{user.rank}</p>
          <h2 className="text-2xl font-semibold text-cream truncate">{user.name}</h2>
        </div>
      </div>
      <div className="mt-5 space-y-2.5 text-sm">
        <div className="flex items-center gap-2 text-cream/80"><Shield size={15} style={{ color: "#E5C76B" }} /> <span className="text-cream/50">Rang:</span> {user.rank}</div>
        <div className="flex items-center gap-2 text-cream/80"><MapPin size={15} style={{ color: "#E5C76B" }} /> <span className="text-cream/50">Region:</span> {user.region === "Alle" ? "Alle Regionen" : user.region}</div>
        {user.leadership && (
          <div className="flex items-center gap-2 text-cream/80"><Crown size={15} style={{ color: "#E5C76B" }} /> <span className="text-cream/50">Rechte:</span> Leitung</div>
        )}
      </div>

      {/* Awards */}
      <div className="mt-6 pt-5 border-t border-white/10" data-testid="portal-awards">
        <h3 className="text-xs uppercase tracking-[0.22em] font-semibold mb-3" style={{ color: "#C69C22" }}>Auszeichnungen</h3>
        {awards === null ? (
          <Loader2 size={18} className="animate-spin text-cream/40" />
        ) : awards.length === 0 ? (
          <p className="text-sm text-cream/40">Noch keine Auszeichnungen.</p>
        ) : (
          <div className="space-y-2.5">
            {awards.map((a) => {
              const Icon = AWARD_ICONS[a.icon] || Medal;
              return (
                <div key={a.id} data-testid={`portal-award-${a.id}`} className="flex items-start gap-3 rounded-xl border border-gold/20 bg-gold/[0.06] px-3 py-2.5">
                  <div className="w-9 h-9 rounded-full border border-gold/40 flex items-center justify-center flex-shrink-0" style={{ background: "rgba(198,156,34,0.12)" }}>
                    <Icon size={17} style={{ color: "#E5C76B" }} />
                  </div>
                  <div className="min-w-0">
                    <p className="text-sm font-semibold text-cream">{a.title}</p>
                    {a.description && <p className="text-xs text-cream/55">{a.description}</p>}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      <button onClick={onLogout} data-testid="portal-logout" className="mt-6 w-full inline-flex items-center justify-center gap-2 text-sm font-semibold rounded-full border border-gold/40 px-4 py-2.5 hover:bg-gold hover:text-anthracite transition-colors" style={{ color: "#E5C76B" }}>
        <LogOut size={16} /> Abmelden
      </button>
    </div>
  );
};

const EventsView = () => {
  const [events, setEvents] = useState(null);
  const [busy, setBusy] = useState(null);

  const load = useCallback(() => {
    axios.get(`${API}/portal/events`, { headers: authHeader() })
      .then(({ data }) => setEvents(data))
      .catch(() => setEvents([]));
  }, []);

  useEffect(() => { load(); }, [load]);

  const rsvp = async (eventId, status) => {
    setBusy(eventId);
    // optimistic update
    setEvents((prev) => prev.map((e) => e.id === eventId
      ? { ...e, my_rsvp: status, going_count: (e.going_count || 0) + (status === "going" && e.my_rsvp !== "going" ? 1 : status !== "going" && e.my_rsvp === "going" ? -1 : 0) }
      : e));
    try {
      await axios.post(`${API}/portal/events/${eventId}/rsvp`, { status }, { headers: authHeader() });
      toast.success(status === "going" ? "Du nimmst teil." : "Du hast dich abgemeldet.");
    } catch {
      toast.error("Aktion fehlgeschlagen.");
      load();
    } finally {
      setBusy(null);
    }
  };

  return (
    <div className="rounded-2xl border border-gold/15 bg-white/[0.02] p-6 flex flex-col" data-testid="portal-events">
      <div className="flex items-center gap-2 mb-5">
        <CalendarDays size={18} style={{ color: "#E5C76B" }} />
        <h2 className="text-lg font-semibold text-cream">Events</h2>
      </div>
      {events === null ? (
        <div className="flex items-center justify-center py-16 text-cream/50"><Loader2 size={24} className="animate-spin" /></div>
      ) : events.length === 0 ? (
        <p className="text-sm text-cream/45 py-12 text-center">Aktuell sind keine Events geplant.</p>
      ) : (
        <div className="space-y-4 overflow-y-auto">
          {events.map((ev) => {
            const going = ev.my_rsvp === "going";
            const declined = ev.my_rsvp === "declined";
            return (
              <article key={ev.id} data-testid={`portal-event-${ev.id}`} className="rounded-xl border border-white/10 bg-white/[0.03] p-4">
                <div className="flex items-start justify-between gap-3">
                  <h3 className="font-semibold text-cream">{ev.title}</h3>
                  {ev.going_count > 0 && (
                    <span data-testid={`portal-event-count-${ev.id}`} className="inline-flex items-center gap-1 text-[11px] font-semibold rounded-full border border-gold/30 px-2 py-0.5 flex-shrink-0" style={{ color: "#E5C76B" }}>
                      <Users size={12} /> {ev.going_count}
                    </span>
                  )}
                </div>
                <div className="mt-2 flex flex-wrap gap-x-4 gap-y-1 text-xs text-cream/60">
                  {ev.date && <span className="inline-flex items-center gap-1.5"><Calendar size={13} style={{ color: "#E5C76B" }} /> {fmtDate(ev.date)}</span>}
                  {ev.time && <span className="inline-flex items-center gap-1.5"><Clock size={13} style={{ color: "#E5C76B" }} /> {ev.time}</span>}
                  {ev.location && <span className="inline-flex items-center gap-1.5"><MapPin size={13} style={{ color: "#E5C76B" }} /> {ev.location}</span>}
                </div>
                {ev.description && <p className="mt-2 text-sm text-cream/80 whitespace-pre-wrap">{ev.description}</p>}
                <div className="mt-3 flex items-center gap-2">
                  <button
                    onClick={() => rsvp(ev.id, "going")}
                    disabled={busy === ev.id}
                    data-testid={`portal-event-join-${ev.id}`}
                    className={`inline-flex items-center gap-1.5 text-xs font-semibold rounded-full px-3 py-1.5 border transition-colors disabled:opacity-60 ${going ? "bg-gold text-anthracite border-gold" : "border-gold/40 hover:bg-gold/10"}`}
                    style={going ? {} : { color: "#E5C76B" }}
                  >
                    <Check size={13} /> {going ? "Du nimmst teil" : "Teilnehmen"}
                  </button>
                  <button
                    onClick={() => rsvp(ev.id, "declined")}
                    disabled={busy === ev.id}
                    data-testid={`portal-event-decline-${ev.id}`}
                    className={`inline-flex items-center gap-1.5 text-xs font-semibold rounded-full px-3 py-1.5 border transition-colors disabled:opacity-60 ${declined ? "bg-white/15 text-cream border-white/30" : "border-white/15 text-cream/55 hover:text-cream hover:border-white/30"}`}
                  >
                    <X size={13} /> {declined ? "Abgemeldet" : "Abmelden"}
                  </button>
                </div>
              </article>
            );
          })}
        </div>
      )}
    </div>
  );
};

const CITY_CHANNELS = ["Bern", "Basel", "Lausanne"];

const ChatView = ({ user }) => {
  const baseChannels = user.region === "Alle" ? CITY_CHANNELS : [user.region];
  const channels = user.leadership ? [...baseChannels, "Leitung"] : baseChannels;
  const [channel, setChannel] = useState(channels[0]);
  const isLeitung = channel === "Leitung";
  const [state, setState] = useState({ messages: [], locked: false });
  const [text, setText] = useState("");
  const [sending, setSending] = useState(false);
  const scrollRef = useRef(null);
  const atBottomRef = useRef(true);

  const load = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/portal/chat?channel=${encodeURIComponent(channel)}`, { headers: authHeader() });
      setState({ messages: data.messages, locked: data.locked });
    } catch { /* ignore poll errors */ }
  }, [channel]);

  useEffect(() => {
    setState({ messages: [], locked: false });
    atBottomRef.current = true;
    load();
    const iv = setInterval(load, 2000);
    return () => clearInterval(iv);
  }, [load]);

  useEffect(() => {
    if (atBottomRef.current && scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [state.messages]);

  const onScroll = () => {
    const el = scrollRef.current;
    if (!el) return;
    atBottomRef.current = el.scrollHeight - el.scrollTop - el.clientHeight < 60;
  };

  const send = async (e) => {
    e.preventDefault();
    const value = text.trim();
    if (!value || sending) return;
    setSending(true);
    try {
      await axios.post(`${API}/portal/chat?channel=${encodeURIComponent(channel)}`, { text: value }, { headers: authHeader() });
      setText("");
      atBottomRef.current = true;
      await load();
    } catch (err) {
      toast.error(err.response?.status === 403 ? "Der Chat ist momentan gesperrt." : "Nachricht konnte nicht gesendet werden.");
    } finally {
      setSending(false);
    }
  };

  return (
    <div className={`rounded-2xl border bg-white/[0.02] flex flex-col h-[600px] ${isLeitung ? "border-teal-300/30" : "border-gold/15"}`} data-testid="portal-chat">
      <div className={`px-5 py-4 border-b ${isLeitung ? "border-teal-300/25" : "border-gold/15"}`}>
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <MessageSquare size={18} style={{ color: isLeitung ? "#5EC8B7" : "#E5C76B" }} />
            <h2 className="text-lg font-semibold text-cream">Chat {channel === "Leitung" ? "Leitung" : channel}</h2>
          </div>
          {state.locked && <span className="inline-flex items-center gap-1.5 text-xs text-red-400"><Lock size={13} /> Gesperrt</span>}
        </div>
        {isLeitung && (
          <p className="mt-1.5 inline-flex items-center gap-1.5 text-[11px] font-semibold uppercase tracking-[0.18em]" style={{ color: "#5EC8B7" }} data-testid="portal-chat-leitung-banner">
            <Crown size={12} /> Interner Leiter-Chat
          </p>
        )}
        {channels.length > 1 && (
          <div className="flex gap-2 mt-3" data-testid="portal-chat-channels">
            {channels.map((c) => {
              const isLead = c === "Leitung";
              const activeC = channel === c;
              return (
                <button
                  key={c}
                  onClick={() => setChannel(c)}
                  data-testid={`portal-chat-channel-${c}`}
                  className={`px-3 py-1.5 rounded-full text-xs font-semibold border transition-colors ${
                    activeC
                      ? (isLead ? "bg-teal-400/90 text-anthracite border-teal-400" : "bg-gold text-anthracite border-gold")
                      : (isLead ? "border-teal-300/30 text-teal-200/70 hover:text-teal-100" : "border-white/15 text-cream/65 hover:text-cream")
                  }`}
                >
                  {c === "Leitung" ? "Leitung" : `Kreis ${c}`}
                </button>
              );
            })}
          </div>
        )}
      </div>

      <div ref={scrollRef} onScroll={onScroll} className="flex-1 overflow-y-auto px-4 py-4 space-y-3" data-testid="portal-chat-messages">
        {state.messages.length === 0 ? (
          <p className="text-sm text-cream/40 text-center py-10">Noch keine Nachrichten. Schreib die erste!</p>
        ) : (
          state.messages.map((m) => {
            const mine = m.user_id === user.id;
            const mineCls = isLeitung ? "bg-teal-400/15 border border-teal-300/30" : "bg-gold/20 border border-gold/30";
            const otherCls = isLeitung ? "bg-teal-400/[0.06] border border-teal-300/15" : "bg-white/[0.05] border border-white/10";
            return (
              <div key={m.id} className={`flex ${mine ? "justify-end" : "justify-start"}`}>
                <div className={`max-w-[80%] rounded-2xl px-4 py-2.5 ${mine ? mineCls : otherCls}`}>
                  {!mine && (
                    <p className="text-[11px] font-semibold mb-0.5">
                      <span style={{ color: isLeitung ? "#5EC8B7" : "#C69C22" }}>{m.user_rank}</span> <span className="text-cream/80">{m.user_name}</span>
                    </p>
                  )}
                  <p className="text-sm text-cream/90 whitespace-pre-wrap break-words">{m.text}</p>
                  <p className="text-[10px] text-cream/40 mt-1 text-right">{fmtTime(m.created_at)}</p>
                </div>
              </div>
            );
          })
        )}
      </div>

      <form onSubmit={send} className={`flex items-center gap-2 p-3 border-t ${isLeitung ? "border-teal-300/25" : "border-gold/15"}`}>
        <Input
          value={text}
          onChange={(e) => setText(e.target.value)}
          disabled={state.locked}
          placeholder={state.locked ? "Chat ist gesperrt" : "Nachricht schreiben…"}
          data-testid="portal-chat-input"
          className={`h-11 rounded-xl bg-white/5 text-cream placeholder:text-cream/40 ${isLeitung ? "border-teal-300/25 focus-visible:ring-teal-300/40 focus-visible:border-teal-300/60" : "border-white/15 focus-visible:ring-gold/40 focus-visible:border-gold/60"}`}
        />
        <button type="submit" disabled={state.locked || sending || !text.trim()} data-testid="portal-chat-send" className={`!px-4 !py-2.5 rounded-full font-semibold inline-flex items-center justify-center disabled:opacity-50 disabled:cursor-not-allowed ${isLeitung ? "bg-teal-400 text-anthracite hover:bg-teal-300" : "btn-primary"}`}>
          {sending ? <Loader2 size={18} className="animate-spin" /> : <Send size={18} />}
        </button>
      </form>
    </div>
  );
};

const AnnouncementsView = ({ user }) => {
  const [data, setData] = useState(null);
  const [channel, setChannel] = useState(null);
  const [text, setText] = useState("");
  const [sending, setSending] = useState(false);
  const scrollRef = useRef(null);
  const atBottomRef = useRef(true);

  const load = useCallback(async () => {
    try {
      const url = channel ? `${API}/portal/announcements?region=${encodeURIComponent(channel)}` : `${API}/portal/announcements`;
      const { data } = await axios.get(url, { headers: authHeader() });
      setData(data);
      if (!channel && data.region) setChannel(data.region);
    } catch { /* ignore poll errors */ }
  }, [channel]);

  useEffect(() => {
    load();
    const iv = setInterval(load, 3000);
    return () => clearInterval(iv);
  }, [load]);

  useEffect(() => {
    if (atBottomRef.current && scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [data]);

  const onScroll = () => {
    const el = scrollRef.current;
    if (!el) return;
    atBottomRef.current = el.scrollHeight - el.scrollTop - el.clientHeight < 60;
  };

  const send = async (e) => {
    e.preventDefault();
    const value = text.trim();
    const region = data?.region || channel;
    if (!value || sending || !region) return;
    setSending(true);
    try {
      await axios.post(`${API}/portal/announcements?region=${encodeURIComponent(region)}`, { text: value }, { headers: authHeader() });
      setText("");
      atBottomRef.current = true;
      await load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Ankündigung konnte nicht gesendet werden.");
    } finally {
      setSending(false);
    }
  };

  const remove = async (id) => {
    try {
      await axios.delete(`${API}/portal/announcements/${id}`, { headers: authHeader() });
      await load();
      toast.success("Ankündigung gelöscht.");
    } catch {
      toast.error("Löschen fehlgeschlagen.");
    }
  };

  if (data === null) {
    return (
      <div className="rounded-2xl border border-gold/30 bg-gold/[0.04] p-6 flex items-center justify-center h-40" data-testid="portal-announcements">
        <Loader2 size={22} className="animate-spin" style={{ color: "#E5C76B" }} />
      </div>
    );
  }

  const channels = data.visible_regions || [];

  return (
    <div className="rounded-2xl border border-gold/35 bg-gold/[0.05] flex flex-col" data-testid="portal-announcements" style={{ boxShadow: "inset 0 0 60px rgba(198,156,34,0.05)" }}>
      <div className="px-5 py-4 border-b border-gold/25">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Megaphone size={18} style={{ color: "#E5C76B" }} />
            <h2 className="text-lg font-semibold font-heading tracking-tight" style={{ color: "#E5C76B" }}>Ankündigungen {channel ? `· ${channel}` : ""}</h2>
          </div>
          {data.locked && <span className="inline-flex items-center gap-1.5 text-xs text-red-400"><Lock size={13} /> Gesperrt</span>}
        </div>
        {channels.length > 1 && (
          <div className="flex flex-wrap gap-2 mt-3" data-testid="portal-announce-channels">
            {channels.map((c) => (
              <button
                key={c}
                onClick={() => { setChannel(c); setData(null); }}
                data-testid={`portal-announce-channel-${c}`}
                className={`px-3 py-1.5 rounded-full text-xs font-semibold border transition-colors ${
                  channel === c ? "bg-gold text-anthracite border-gold" : "border-gold/30 text-cream/65 hover:text-cream"
                }`}
              >
                Kreis {c}
              </button>
            ))}
          </div>
        )}
      </div>

      <div ref={scrollRef} onScroll={onScroll} className="flex-1 overflow-y-auto px-4 py-4 space-y-3 max-h-[360px]" data-testid="portal-announce-messages">
        {data.messages.length === 0 ? (
          <p className="text-sm text-cream/45 text-center py-10">Noch keine Ankündigungen in diesem Kreis.</p>
        ) : (
          data.messages.map((m) => {
            const mine = m.author_id === user.id;
            return (
              <div key={m.id} data-testid={`portal-announce-${m.id}`} className="rounded-xl border border-gold/30 bg-gold/[0.10] px-4 py-3">
                <div className="flex items-center justify-between gap-2">
                  <p className="text-[11px] font-semibold">
                    <span style={{ color: "#E5C76B" }}>{m.author_rank}</span> <span className="text-cream/85">{m.author_name}</span>
                  </p>
                  <div className="flex items-center gap-2">
                    <span className="text-[10px] text-cream/45">{fmtTime(m.created_at)}</span>
                    {(data.can_post || mine) && (
                      <button onClick={() => remove(m.id)} data-testid={`portal-announce-delete-${m.id}`} aria-label="Löschen" className="text-cream/40 hover:text-red-400 transition-colors"><Trash2 size={13} /></button>
                    )}
                  </div>
                </div>
                <p className="mt-1 text-[15px] font-heading font-medium text-cream leading-snug whitespace-pre-wrap break-words">{m.text}</p>
              </div>
            );
          })
        )}
      </div>

      {data.can_post ? (
        <form onSubmit={send} className="flex items-center gap-2 p-3 border-t border-gold/25">
          <Input
            value={text}
            onChange={(e) => setText(e.target.value)}
            disabled={data.locked}
            placeholder={data.locked ? "Kanal gesperrt" : "Ankündigung schreiben…"}
            data-testid="portal-announce-input"
            className="h-11 rounded-xl bg-gold/[0.06] border-gold/30 text-cream placeholder:text-cream/40 focus-visible:ring-gold/40 focus-visible:border-gold/60 font-heading"
          />
          <button type="submit" disabled={data.locked || sending || !text.trim()} data-testid="portal-announce-send" className="bg-gold text-anthracite hover:bg-gold-soft !px-4 !py-2.5 rounded-full font-semibold inline-flex items-center justify-center disabled:opacity-50 disabled:cursor-not-allowed transition-colors">
            {sending ? <Loader2 size={18} className="animate-spin" /> : <Send size={18} />}
          </button>
        </form>
      ) : (
        <p className="px-5 py-3 border-t border-gold/20 text-xs text-cream/45 text-center" data-testid="portal-announce-readonly">
          Nur Vertreter & Ankündigungs-Admins können hier schreiben.
        </p>
      )}
    </div>
  );
};


const Portal = () => {
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [confirming, setConfirming] = useState(false);

  useEffect(() => {
    const token = localStorage.getItem(PORTAL_TOKEN_KEY);
    if (!token) { navigate("/"); return; }
    axios.get(`${API}/portal/me`, { headers: { Authorization: `Bearer ${token}` } })
      .then(({ data }) => setUser(data))
      .catch((err) => {
        localStorage.removeItem(PORTAL_TOKEN_KEY);
        if (err.response?.status === 403) toast.error("Dein Konto wurde gesperrt. Bitte wende dich an die Leitung.");
        navigate("/");
      });
  }, [navigate]);

  const logout = () => { localStorage.removeItem(PORTAL_TOKEN_KEY); navigate("/"); };

  const confirmDevice = async () => {
    setConfirming(true);
    try {
      await axios.post(`${API}/portal/confirm-device`, {}, { headers: authHeader() });
      setUser((u) => ({ ...u, security_banner: false }));
      toast.success("Bestätigt. Willkommen zurück!");
    } catch {
      toast.error("Bestätigung fehlgeschlagen. Bitte erneut versuchen.");
    } finally {
      setConfirming(false);
    }
  };

  if (!user) {
    return <div className="min-h-screen flex items-center justify-center bg-anthracite text-cream"><Loader2 size={28} className="animate-spin" style={{ color: "#E5C76B" }} /></div>;
  }

  if (user.security_banner) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-anthracite px-6" data-testid="portal-security-banner">
        <div className="pointer-events-none absolute -top-32 left-1/2 -translate-x-1/2 w-[700px] h-[400px]" style={{ background: "radial-gradient(ellipse, rgba(198,156,34,0.12) 0%, transparent 60%)" }} />
        <div className="relative w-full max-w-md bg-anthracite-soft border border-gold/25 rounded-3xl p-8 lg:p-10 shadow-2xl shadow-black/40 text-center">
          <div className="flex items-center justify-center w-14 h-14 rounded-full border border-gold/40 mx-auto" style={{ background: "rgba(198,156,34,0.1)" }}>
            <ShieldAlert size={24} style={{ color: "#E5C76B" }} />
          </div>
          <h1 className="mt-6 text-2xl font-semibold text-cream tracking-tight">Login von neuem Gerät erkannt</h1>
          <p className="mt-3 text-sm text-cream/65 leading-relaxed">
            Aus Sicherheitsgründen wurde bei deinem Konto eine Anmeldung von einem neuen Gerät oder Standort festgestellt.
            Bitte bestätige, dass du das warst, um fortzufahren.
          </p>
          <button
            onClick={confirmDevice}
            disabled={confirming}
            data-testid="portal-confirm-device"
            className="btn-primary mt-8 w-full disabled:opacity-70"
          >
            {confirming ? <><Loader2 size={18} className="animate-spin" /> Wird bestätigt…</> : <><Check size={18} /> Das war ich – bestätigen</>}
          </button>
          <button onClick={logout} data-testid="portal-security-logout" className="mt-4 text-sm text-cream/50 hover:text-cream transition-colors">
            Das war ich nicht – abmelden
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-anthracite text-cream">
      <header className="border-b border-gold/15 bg-anthracite/90 backdrop-blur-xl">
        <div className="container-boe flex items-center justify-between h-18 py-4">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-full bg-anthracite-soft border border-gold/40 flex items-center justify-center">
              <span className="font-accent italic text-xl font-semibold" style={{ color: "#E5C76B" }}>B</span>
            </div>
            <div className="flex flex-col leading-tight">
              <span className="font-semibold text-cream tracking-tight text-sm">Mitglieder-Portal</span>
              <span className="text-[10px] uppercase tracking-[0.25em] text-cream/50">Brotherhood of Eden</span>
            </div>
          </div>
        </div>
      </header>

      <main className="container-boe py-8">
        <div className="grid gap-6 lg:grid-cols-12">
          <div className="lg:col-span-3"><ProfileCard user={user} onLogout={logout} /></div>
          <div className="lg:col-span-5 space-y-6">
            <EventsView />
            <AnnouncementsView user={user} />
          </div>
          <div className="lg:col-span-4"><ChatView user={user} /></div>
        </div>
      </main>
    </div>
  );
};

export default Portal;
